import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Link,
  IconButton,
  Box,
  Autocomplete,
  FormHelperText,
  InputAdornment
} from '@mui/material';
import { Eye, EyeOff } from 'lucide-react';
import api from '../axios/api';
import { useNavigate } from 'react-router-dom';
import { showNotification } from '../redux/slices/notificationSlice';
import { useDispatch } from 'react-redux';

// Signup Schema
const signupSchema = yup.object().shape({
  firstName: yup
    .string()
    .min(2, 'First Name must be at least 2 characters')
    .max(50, 'First Name must be less than 50 characters')
    .required('First Name is required'),
  lastName: yup
    .string()
    .min(2, 'Last Name must be at least 2 characters')
    .max(50, 'Last Name must be less than 50 characters')
    .required('Last Name is required'),
  email: yup
    .string()
    .email('Invalid email address')
    .required('Email is required'),
  countryCode: yup
    .string()
    .required('Country Code is required'),
  phoneNo: yup
    .string()
    .matches(/^\d{10}$/, 'Phone number must be exactly 10 digits')
    .required('Phone number is required'),
  // phoneNo: yup
  // .string()
  // .matches(/^[0-9]{7,15}$/, 'Invalid phone number')
  // .required('Phone number is required'),
  password: yup
    .string()
    .min(8, 'Password must be at least 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
      'Password must include uppercase, lowercase, number, and special character'
    )
    .required('Password is required'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password')], 'Passwords do not match')
    .required('Confirm Password is required')
});

const Signup = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword1, setShowPassword1] = useState(false);
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(signupSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNo: '',
      countryCode: '',
      password: '',
      confirmPassword: ''
    }
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get("/common/countryDrop");
        const formattedCountries = response.data.data.map((item) => ({
          key: item.value,
          value: item.telephone_code,
          label: `(${item.telephone_code}) ${item.country_code}`,
          countryName: item.label, // Include full country name for search
          searchText: `${item.label || item.label || ''} ${item.country_code} ${item.telephone_code}` // Combined search text
        }));
        setCountries(formattedCountries);
        // if (formattedCountries.length > 0) {
        //   setSelectedCountry(formattedCountries[0]);
        //   setValue('countryCode', formattedCountries[0].value);
        // }
      } catch (error) {
        dispatch(
          showNotification({
            message: 'Failed to load country data',
            type: "error",
          })
        );
      }
    };

    fetchData();
  }, [setValue]);

  const onSubmit = async (data) => {
    try {
      const selectedCountry = countries.find(
        country => country.value === data.countryCode
      );
      const formData = {
        ...data,
        countryId: selectedCountry?.key
      };

      // Replace with your signup API call
      const response = await api.post('/auth/register', formData);

      if (response.data.status === "200") {
        dispatch(
          showNotification({
            message: 'Signup Successful',
            type: "success",
          })
        );
        navigate("/login")
      } else {
        dispatch(
          showNotification({
            message: 'Signup Failed',
            type: "error",
          })
        );
      }
    } catch (error) {
      dispatch(
        showNotification({
          message: error?.response?.data?.error || 'An error occurred during signup',
          type: "error",
        })
      );
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f5f5f5',
        padding: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          width: { xs: '90%', md: '80%', lg: '70%' },
          backgroundColor: 'white',
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: 3,
        }}
      >
        {/* Image Section */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'white',
            padding: 2,
          }}
        >
          <img
            src="/vectors/6465150.jpg"
            alt="Login Vector"
            style={{
              width: '100%',
              height: 'auto',
              objectFit: 'contain',
            }}
          />
        </Box>

        {/* Form Section */}
        <Card
          sx={{
            flex: 1,
            padding: 3,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <CardContent>
            <Typography variant="h4" gutterBottom>
              Create Your Account
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Fill out the details to get started
            </Typography>

            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={
                      <span>
                        First Name
                        <span style={{ color: 'red' }}> *</span>
                      </span>
                    }
                    {...register('firstName')}
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={
                      <span>
                        Last Name
                        <span style={{ color: 'red' }}> *</span>
                      </span>
                    }
                    {...register('lastName')}
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={
                      <span>
                        Email Address
                        <span style={{ color: 'red' }}> *</span>
                      </span>
                    }
                    type="email"
                    {...register('email')}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={countries}
                    value={selectedCountry}
                    onChange={(_, newValue) => {
                      setSelectedCountry(newValue);
                      setValue('countryCode', newValue?.value || '');
                    }}
                    getOptionLabel={(option) => option.label}
                    filterOptions={(options, { inputValue }) => {
                      const searchText = inputValue.toLowerCase();
                      return options.filter(option => 
                        option.searchText.toLowerCase().includes(searchText)
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={
                          <span>
                            Select Country
                            <span style={{ color: 'red' }}>*</span>
                          </span>
                        }
                        error={!!errors.countryCode}
                        helperText={errors.countryCode?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={
                      <span>
                        Phone Number
                        <span style={{ color: 'red' }}> *</span>
                      </span>
                    }
                    type="Number"
                    {...register('phoneNo')}
                    error={!!errors.phoneNo}
                    helperText={errors.phoneNo?.message}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={
                      <span>
                        Password
                        <span style={{ color: 'red' }}> *</span>
                      </span>
                    }
                    type={showPassword1 ? 'text' : 'password'}
                    {...register('password')}
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword1(!showPassword1)}
                            edge="end"
                          >
                            {showPassword1 ? <EyeOff size={20} /> : <Eye size={20} />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={
                      <span>
                        Confirm Password
                        <span style={{ color: 'red' }}> *</span>
                      </span>
                    }
                    type={showPassword ? 'text' : 'password'}
                    {...register('confirmPassword')}
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword?.message}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormHelperText>
                    At least 8 characters, including uppercase, lowercase, number, and special character
                  </FormHelperText>
                </Grid>

                <Grid item xs={12}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                  >
                    Create Account
                  </Button>
                </Grid>

                <Grid item xs={12} textAlign="center">
                  <Typography variant="body2">
                    Already have an account?{' '}
                    <Link href="/login" underline="hover">
                      Log In
                    </Link>
                  </Typography>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default Signup;